// goldenRatio.ts — the single implementation of the protein-blend ratio check.
//
// Two screens use this: the full Golden Ratio analyzer, and the live card in the
// App view. They MUST share this module. BUGS.md #9 records what happens in this
// codebase when a calculation gets a second implementation: four engines that
// disagree. Do not copy any of this into a component.
//
// The ratio splits the protein system into three target buckets — hydrolysed
// protein, isolated protein, pulse flour — and reports the split two ways,
// because the source spec is ambiguous about whether its 25/55/20 means a ratio
// of ingredient WEIGHT or a ratio of PROTEIN CONTRIBUTED. Both are shown; the
// caller does not have to pick.
//
// Concentrate is deliberately its own bucket, outside the ratio. A protein
// concentrate is not an isolate, and folding it in silently is how a formula
// scores well against a target it does not actually meet.

export type Bucket = "hydro" | "isolate" | "pulse" | "concentrate" | "none";

/** Only these three are summed into the ratio. */
export const COUNTED: Bucket[] = ["hydro", "isolate", "pulse"];

/** `ingredients.protein_class` is authoritative when set. */
export const CLASS_TO_BUCKET: Record<string, Bucket> = {
  hydrolysed_protein: "hydro",
  isolated_protein: "isolate",
  pulse_flour: "pulse",
  protein_concentrate: "concentrate",
  other_flour: "none",
  other_protein: "none",
  not_protein: "none",
};

const HYDRO_RE = /hydroly/i;
const ISOLATE_RE = /isolate/i;
const CONCENTRATE_RE = /concentrate/i;
const FORM_RE = /flour|powder|grits|granulate/i;
const PULSE_RE = /chickpea|lupin|pea|lentil|fava|faba|bean|mung|pulse|gram/i;

export function normalizeTags(tags: string[] | string | null | undefined): string[] {
  if (!tags) return [];
  const list = Array.isArray(tags) ? tags : [tags];
  return list.map((x) => String(x).trim().toLowerCase());
}

/** Best guess from the label when protein_class is NULL. Never trusted: protein
 *  percent alone cannot separate a 68% concentrate from an isolate, which is
 *  exactly where this used to get it wrong. */
export function guessBucket(
  name: string,
  id: string,
  proteinPer100g: number,
  tags: string[] | string | null | undefined
): Bucket {
  const tagList = normalizeTags(tags);
  if (tagList.indexOf("hydrolyzed protein") >= 0 || tagList.indexOf("hydrolysed protein") >= 0) {
    return "hydro";
  }
  if (tagList.indexOf("isolated protein") >= 0) return "isolate";

  const hay = `${name} ${id}`;
  if (HYDRO_RE.test(hay)) return "hydro";
  if (ISOLATE_RE.test(hay)) return "isolate";
  if (CONCENTRATE_RE.test(hay)) return "concentrate";
  if (FORM_RE.test(hay) && PULSE_RE.test(hay)) return "pulse";
  return "none";
}

export function classify(
  name: string,
  id: string,
  proteinPer100g: number,
  tags: string[] | string | null | undefined,
  proteinClass: string | null | undefined
): { bucket: Bucket; trusted: boolean } {
  if (proteinClass && CLASS_TO_BUCKET[proteinClass]) {
    return { bucket: CLASS_TO_BUCKET[proteinClass], trusted: true };
  }
  return { bucket: guessBucket(name, id, proteinPer100g, tags), trusted: false };
}

/** One ingredient's contribution. `weight` is any consistent unit — grams, or a
 *  normalised share. The ratio is scale-free, so only relative values matter. */
export interface BlendItem {
  id: string;
  name: string;
  weight: number;
  proteinPer100g: number;
  tags?: string[] | string | null;
  proteinClass?: string | null;
  /** Set to override the classification; used by the analyzer's per-row picker. */
  bucketOverride?: Bucket;
}

export interface BlendSummary {
  weight: Record<Bucket, number>;
  protein: Record<Bucket, number>;
  weightSum: number;
  proteinSum: number;
  /** Rows whose bucket was guessed AND could move the answer. */
  guessCount: number;
  /** Percent of the counted blend, by ingredient weight. */
  pctByWeight: Record<Bucket, number>;
  /** Percent of the counted blend, by protein contributed. */
  pctByProtein: Record<Bucket, number>;
}

function zeroed(): Record<Bucket, number> {
  return { hydro: 0, isolate: 0, pulse: 0, concentrate: 0, none: 0 };
}

/** Duplicate ids are summed. Never key an accumulator by ingredient NAME — that
 *  bug already exists elsewhere in this app and silently drops rows. */
export function summariseBlend(items: BlendItem[]): BlendSummary {
  const weight = zeroed();
  const protein = zeroed();
  let guessCount = 0;

  for (const it of items) {
    let bucket: Bucket;
    let trusted: boolean;
    if (it.bucketOverride) {
      bucket = it.bucketOverride;
      trusted = true;
    } else {
      const c = classify(it.name, it.id, it.proteinPer100g, it.tags, it.proteinClass);
      bucket = c.bucket;
      trusted = c.trusted;
    }

    // A guess only matters if it lands in a counted bucket, or carries enough
    // protein that reclassifying it would move the number. Otherwise water and
    // salt would make every formula read "provisional", which is noise.
    if (!trusted && (COUNTED.indexOf(bucket) >= 0 || it.proteinPer100g >= 50)) {
      guessCount += 1;
    }

    weight[bucket] += it.weight;
    protein[bucket] += (it.weight * it.proteinPer100g) / 100;
  }

  const weightSum = COUNTED.reduce((s, b) => s + weight[b], 0);
  const proteinSum = COUNTED.reduce((s, b) => s + protein[b], 0);

  const pctByWeight = zeroed();
  const pctByProtein = zeroed();
  for (const b of COUNTED) {
    pctByWeight[b] = weightSum > 0 ? (weight[b] / weightSum) * 100 : 0;
    pctByProtein[b] = proteinSum > 0 ? (protein[b] / proteinSum) * 100 : 0;
  }

  return { weight, protein, weightSum, proteinSum, guessCount, pctByWeight, pctByProtein };
}
